<div class="floating-toc" [class.floating-toc--open]="isOpen">
  <button
    type="button"
    class="floating-toc__toggle"
    (click)="toggleOpen()"
    [attr.aria-expanded]="isOpen"
    aria-controls="floatingTocPanel"
    aria-label="Toggle quick guide"
  >
    <span
      class="floating-toc__icon"
      [class.floating-toc__icon--open]="isOpen"
      aria-hidden="true"
    >
      <span class="floating-toc__icon-label">{{ isOpen ? '×' : 'HELP' }}</span>
    </span>
    <span
      *ngIf="globalFreshnessIndicatorVariant"
      class="floating-toc__toggle-indicator"
      [class.floating-toc__toggle-indicator--new]="globalFreshnessIndicatorVariant === 'allFresh'"
      [class.floating-toc__toggle-indicator--updates]="globalFreshnessIndicatorVariant === 'containsFresh'"
      [attr.title]="globalFreshnessIndicatorLabel"
    >
      <span aria-hidden="true" class="floating-toc__toggle-indicator-star">★</span>
      <span class="floating-toc__sr-only">{{ globalFreshnessIndicatorLabel }}</span>
    </span>
  </button>

  <div
    id="floatingTocPanel"
    class="floating-toc__panel"
    [class.floating-toc__panel--open]="isOpen"
    role="dialog"
    aria-modal="false"
    aria-label="Quick guide"
  >
    <div class="floating-toc__hero">
      <span class="floating-toc__hero-icon" aria-hidden="true">🤝</span>
      <div class="floating-toc__hero-copy">
        <h2 class="floating-toc__hero-title">Don't worry, I'll help you.</h2>
        <p class="floating-toc__hero-subtitle">Your guided walk-through is right here.</p>
      </div>
    </div>

    <div class="floating-toc__header">
      <div class="floating-toc__search-wrapper">
        <input
          type="text"
          class="floating-toc__search"
          placeholder="Search the guide"
          aria-label="Search tours"
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearchChange($event)"
        />
        <button
          type="button"
          class="floating-toc__search-clear"
          *ngIf="hasSearchQuery"
          (click)="clearSearch()"
          aria-label="Clear search"
        ></button>
      </div>
    </div>

    <div class="floating-toc__content">
      <div class="floating-toc__controls" *ngIf="!hasSearchQuery">
        <label class="floating-toc__control-switch">
          <input
            type="checkbox"
            class="floating-toc__control-switch-input"
            [checked]="sortNewestFirst"
            (change)="onSortNewestFirstToggle($event)"
          />
          <span class="floating-toc__control-switch-label">Newest first</span>
        </label>
        <button type="button" class="floating-toc__control" (click)="toggleAll()">
          {{ allExpanded ? 'Collapse all' : 'Expand all' }}
        </button>
      </div>

      <div class="floating-toc__search-results" *ngIf="hasSearchQuery">
        <ng-container *ngIf="searchResults.length; else emptySearch">
          <ul class="floating-toc__search-list">
            <li class="floating-toc__search-item" *ngFor="let result of searchResults; trackBy: trackSearchResult">
              <button type="button" class="floating-toc__search-button" (click)="startSearchResult($event, result)">
                <span class="floating-toc__search-title" [innerHTML]="highlight(result.scenarioLabel)"></span>
                <span class="floating-toc__search-meta">
                  <span class="floating-toc__search-meta-text">Step {{ result.stepIndex + 1 }} · <span [innerHTML]="highlight(result.stepTitle)"></span></span>
                  <span
                    *ngIf="result.freshnessBadgeText"
                    [ngClass]="[
                      'floating-toc__section-badge',
                      result.freshness ? 'floating-toc__section-badge--' + result.freshness : 'floating-toc__section-badge--recent'
                    ]"
                    [attr.title]="result.freshnessLabel"
                  >
                    <span aria-hidden="true" class="floating-toc__section-badge-icon">★</span>
                    <span class="floating-toc__section-badge-text">{{ result.freshnessBadgeText }}</span>
                    <span class="floating-toc__sr-only">{{ result.freshnessLabel }}</span>
                  </span>
                </span>
                <span class="floating-toc__search-snippet" *ngIf="result.snippet" [innerHTML]="highlight(result.snippet)"></span>
              </button>
            </li>
          </ul>
        </ng-container>
        <ng-template #emptySearch>
          <p class="floating-toc__search-empty">No results found. Try a different phrase.</p>
        </ng-template>
      </div>

      <div class="floating-toc__chapters" *ngIf="!hasSearchQuery">
        <div
          class="floating-toc__chapter"
          *ngFor="let chapter of chapters"
          [class.floating-toc__chapter--expanded]="chapter.expanded"
        >
          <div class="floating-toc__chapter-header-wrapper">
            <button
              type="button"
              class="floating-toc__chapter-header"
              (click)="onChapterHeaderClick($event, chapter)"
              [attr.aria-expanded]="chapter.expanded"
            >
              <span class="floating-toc__chevron" [class.floating-toc__chevron--open]="chapter.expanded"></span>
              <span class="floating-toc__chapter-title">
                <span class="floating-toc__chapter-title-text">{{ chapter.title }}</span>
                <span
                  *ngIf="chapter.freshnessBadgeText"
                  class="floating-toc__chapter-badge"
                  [class.floating-toc__chapter-badge--new]="chapter.freshnessBadgeVariant === 'allFresh'"
                  [class.floating-toc__chapter-badge--updates]="chapter.freshnessBadgeVariant === 'containsFresh'"
                  [attr.title]="chapter.freshnessBadgeLabel"
                >
                  <span aria-hidden="true" class="floating-toc__chapter-badge-icon">★</span>
                  <span class="floating-toc__chapter-badge-text">{{ chapter.freshnessBadgeText }}</span>
                  <span class="floating-toc__sr-only">{{ chapter.freshnessBadgeLabel }}</span>
                </span>
              </span>
            </button>

            <button
              type="button"
              class="floating-toc__chapter-description"
              *ngIf="chapter.description"
              tabindex="-1"
              (click)="onChapterDescriptionClick($event, chapter)"
            >
              {{ chapter.description }}
            </button>
          </div>

          <ul
            class="floating-toc__sections"
            [class.floating-toc__sections--open]="chapter.expanded"
            [class.floating-toc__sections--open-all]="chapter.expanded && chapter.showAllSections"
          >
            <li class="floating-toc__section" *ngFor="let section of chapter.sections">
              <button
                type="button"
                class="floating-toc__section-button"
                (click)="onSectionButtonClick($event, chapter, section)"
              >
                <span class="floating-toc__section-index">{{ section.stepIndex + 1 }}</span>
                <span class="floating-toc__section-title">
                  <span class="floating-toc__section-title-text">{{ section.title }}</span>
                  <span
                    *ngIf="section.freshness"
                    [ngClass]="['floating-toc__section-badge', 'floating-toc__section-badge--' + section.freshness]"
                    [attr.title]="section.freshnessLabel"
                  >
                    <span aria-hidden="true" class="floating-toc__section-badge-icon">★</span>
                    <span class="floating-toc__section-badge-text">{{ section.freshnessBadgeText }}</span>
                    <span class="floating-toc__sr-only">{{ section.freshnessLabel }}</span>
                  </span>
                </span>
              </button>
              <button
                type="button"
                class="floating-toc__section-description"
                *ngIf="section.description"
                tabindex="-1"
                (click)="onSectionDescriptionClick($event, chapter, section)"
              >
                {{ section.description }}
              </button>
            </li>
          </ul>
          <button
            type="button"
            class="floating-toc__sections-toggle"
            *ngIf="chapter.expanded && hasSectionOverflow(chapter)"
            (click)="toggleChapterSections(chapter)"
          >
            <span
              class="floating-toc__sections-toggle-icon"
              [class.floating-toc__sections-toggle-icon--expanded]="chapter.showAllSections"
            ></span>
            <span class="floating-toc__sections-toggle-label">{{
              chapter.showAllSections ? 'Show less' : 'Show more'
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <button
    type="button"
    class="floating-toc__overlay"
    *ngIf="isOpen"
    (click)="toggleOpen()"
    aria-label="Close quick guide overlay"
  ></button>
</div>
